import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Search,
  Trophy,
  Users,
  BarChart3,
  DollarSign,
  Clock,
  CheckCircle2,
  XCircle,
  RotateCcw,
  Flame,
  Target,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import NewtonsCradleLoader from '../../components/common/NewtonsCradleLoader';
import { getThemeColor } from '../../config/appConfig';
import apiService, {
  extractChallengeParticipantsList,
  extractChallengeParticipantsPagination,
} from '../../utils/apiService';
import { useFetchOnceByKey } from '../../hooks/useMountOnce';
import {
  formatChallengeDateTime,
  formatLots,
  formatMoney,
  getChallengePhase,
  getCountdownParts,
  normalizeChallengeOverview,
  normalizeChallengeParticipant,
} from './challengesData';

const THEME = getThemeColor('primary');
const THEME_DARK = '#7a1a1a';
const PAGE_SIZE_OPTIONS = ['10', '25', '50'];
const BANNER_SRC = '/images/challenges/weekly-challenge-banner-landscape.png';
const TERMS_LINK = '/documents/weekly-challenge-terms-and-conditions.pdf';

const PillBox = ({ value, label }) => (
  <div className="flex flex-col items-center justify-center rounded-lg px-2 py-1 min-w-[36px] sm:min-w-[40px] bg-gradient-to-b from-red-700 to-red-950 ring-1 ring-red-500/40">
    <strong className="text-xs sm:text-sm font-black text-white leading-none tabular-nums">
      {String(value).padStart(2, '0')}
    </strong>
    <em className="text-[7px] sm:text-[8px] not-italic uppercase tracking-wide text-white/70 leading-none mt-1">
      {label}
    </em>
  </div>
);

/** Compact "Challenge Ends In" pill — sits next to the page title instead of a full-width card. */
const CountdownPill = ({ phase, countdown }) => (
  <div className="inline-flex items-center gap-2 sm:gap-3 rounded-full bg-[#0b1220] pl-2 pr-3 sm:pr-4 py-2 shadow-lg shrink-0">
    <span className="relative w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center bg-red-600/20 ring-2 ring-red-500/50 shrink-0">
      <Clock className="w-4 h-4 text-red-500" />
    </span>
    <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-white/90 whitespace-nowrap">
      {phase === 'upcoming' ? 'Challenge Starts In' : phase === 'ended' ? 'Challenge Ended' : 'Challenge Ends In'}
    </span>
    {phase !== 'ended' && (
      <div className="flex items-center gap-1">
        <PillBox value={countdown?.days ?? 0} label="Days" />
        <span className="text-white/25 font-bold text-xs">:</span>
        <PillBox value={countdown?.hours ?? 0} label="Hours" />
        <span className="text-white/25 font-bold text-xs">:</span>
        <PillBox value={countdown?.minutes ?? 0} label="Minutes" />
        <span className="text-white/25 font-bold text-xs">:</span>
        <PillBox value={countdown?.seconds ?? 0} label="Seconds" />
      </div>
    )}
  </div>
);

const StatTile = ({ icon: Icon, label, value, tone, card, muted, text }) => (
  <div className={`rounded-2xl border p-5 shadow-sm flex items-center gap-4 transition hover:shadow-md ${card}`}>
    <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: tone }}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <div className="min-w-0">
      <p className={`text-xs font-medium ${muted}`}>{label}</p>
      <p className={`text-2xl font-extrabold tracking-tight truncate ${text}`}>{value}</p>
    </div>
  </div>
);

/** Tinted card used for the two overall-progress panels (orange = remaining, green = earnings target). */
const TintedProgressCard = ({ icon: Icon, tone, label, value, barPct, barLabel, showCheck }) => (
  <div
    className="rounded-2xl p-5"
    style={{ background: `${tone}12`, border: `1px solid ${tone}33` }}
  >
    <div className="flex items-center gap-3 mb-3.5">
      <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: tone }}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium truncate" style={{ color: tone }}>{label}</p>
        <p className="text-xl font-extrabold" style={{ color: tone }}>{value}</p>
      </div>
    </div>
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: `${tone}22` }}>
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${barPct}%`, background: tone }}
        />
      </div>
      {showCheck ? (
        <span className="w-4 h-4 rounded-full flex items-center justify-center shrink-0" style={{ background: tone }}>
          <CheckCircle2 className="w-3 h-3 text-white" />
        </span>
      ) : (
        <span className="text-xs font-semibold w-9 text-right shrink-0" style={{ color: tone }}>{barLabel}</span>
      )}
    </div>
  </div>
);

const DepositBadge = ({ isDeposit }) =>
  isDeposit ? (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
      <CheckCircle2 className="w-3.5 h-3.5" />
      Yes
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
      <XCircle className="w-3.5 h-3.5" />
      No
    </span>
  );

const ChallengesList = () => {
  const { isDarkMode } = useTheme();
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [overview, setOverview] = useState(null);
  const [overviewError, setOverviewError] = useState('');
  const [nowTick, setNowTick] = useState(Date.now());

  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(true);
  const [rowsError, setRowsError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState('10');
  const [pagination, setPagination] = useState({ totalRecords: 0, totalPages: 1 });

  const fetchOverview = useCallback(async ({ cancelled }) => {
    setLoadingOverview(true);
    setOverviewError('');
    try {
      const res = await apiService.getChallengeOverview();
      if (cancelled()) return;
      if (!res.success) {
        setOverviewError(res.error || 'Failed to load challenge details');
        setOverview(null);
      } else {
        setOverview(normalizeChallengeOverview(res.data));
      }
    } catch (err) {
      if (!cancelled()) setOverviewError(err.message || 'Failed to load challenge details');
    } finally {
      if (!cancelled()) setLoadingOverview(false);
    }
  }, []);

  useFetchOnceByKey('challenges-overview-v1', fetchOverview, true);

  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const apiPageSize = Number(pageSize) || 10;
  const rowsFetchKey = `challenge-participants|${currentPage}|${apiPageSize}|${appliedSearchTerm}`;

  const fetchRows = useCallback(
    async ({ cancelled }) => {
      setLoadingRows(true);
      setRowsError('');
      try {
        const res = await apiService.getChallengeParticipants({
          pageNumber: currentPage,
          pageSize: apiPageSize,
          searchTerm: appliedSearchTerm,
        });
        if (cancelled()) return;
        if (!res.success) {
          setRowsError(res.error || 'Failed to load participants');
          setRows([]);
        } else {
          setRows(extractChallengeParticipantsList(res).map(normalizeChallengeParticipant));
          setPagination(extractChallengeParticipantsPagination(res, apiPageSize));
        }
      } catch (err) {
        if (!cancelled()) setRowsError(err.message || 'Failed to load participants');
      } finally {
        if (!cancelled()) setLoadingRows(false);
      }
    },
    [currentPage, apiPageSize, appliedSearchTerm]
  );

  useFetchOnceByKey(rowsFetchKey, fetchRows, true);

  const phase = useMemo(
    () => (overview ? getChallengePhase(overview.startDate, overview.endDate, nowTick) : 'upcoming'),
    [overview, nowTick]
  );
  const countdown = useMemo(
    () => (overview ? getCountdownParts(phase === 'upcoming' ? overview.startDate : overview.endDate) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [overview, phase, nowTick]
  );

  const handleApplyFilters = () => {
    setAppliedSearchTerm(searchTerm.trim());
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setAppliedSearchTerm('');
    setCurrentPage(1);
  };

  const hasActiveFilters = Boolean(appliedSearchTerm);

  // Overall (all accounts combined) progress toward the challenge's single 50-lot target —
  // not per-account. Derived from the same totals already shown in the tiles above.
  const overallRemainingLots = overview ? Math.max(0, overview.minimumLots - overview.totalLots) : 0;
  const overallProgressPct = overview && overview.minimumLots > 0
    ? Math.min(100, Math.round((overview.totalLots / overview.minimumLots) * 100))
    : 0;

  const totalPages = Math.max(1, Number(pagination.totalPages) || 1);
  const totalRecords = pagination.totalRecords || rows.length;
  const showingFrom = totalRecords === 0 ? 0 : (currentPage - 1) * apiPageSize + 1;
  const showingTo = Math.min(currentPage * apiPageSize, totalRecords);

  const card = isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100';
  const text = isDarkMode ? 'text-white' : 'text-gray-900';
  const muted = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const inputCls = `w-full rounded-xl border px-3 py-2.5 text-sm outline-none transition focus:ring-2 ${
    isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
  }`;

  return (
    // Extra bottom padding keeps the fixed WhatsApp bubble (bottom-right) clear of the
    // pagination controls below the table.
    <div className={`min-h-full pb-28 ${isDarkMode ? 'bg-black' : 'bg-white'}`}>
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-5 sm:py-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0"
              style={{ background: `linear-gradient(135deg, ${THEME}, ${THEME_DARK})` }}
            >
              <Flame className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className={`text-2xl font-bold ${text}`}>Challenges</h1>
              <p className={`text-sm ${muted}`}>
                <Link to="/" className="hover:text-red-800">Dashboard</Link>
                <span className="mx-1.5">›</span>
                <span>Challenges List</span>
              </p>
            </div>
          </div>
          {overview ? (
            <div className="flex flex-col items-end gap-2">
              <CountdownPill phase={phase} countdown={countdown} />
              <p className={`text-xs flex items-center gap-1.5 ${muted}`}>
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 text-amber-500" />
                Please read the{' '}
                <a
                  href={TERMS_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold underline hover:no-underline"
                  style={{ color: THEME }}
                >
                  terms and conditions
                </a>
                {' '}carefully.
              </p>
            </div>
          ) : null}
        </div>

        {overviewError ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-sm px-4 py-3 mb-6 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            {overviewError}
          </div>
        ) : null}

        {/* Banner — capped max-width (not height) so it scales down as one proportional block,
            no cropping and no giant empty gutters on the sides. */}
        <div className={`rounded-2xl border overflow-hidden shadow-sm mb-4 ${card}`}>
          <img
            src={BANNER_SRC}
            alt="ABET Partners Weekly Challenge"
            className="block w-full h-auto"
          />
        </div>

        {loadingOverview ? (
          <div className={`rounded-2xl border p-10 flex justify-center mb-6 ${card}`}>
            <NewtonsCradleLoader />
          </div>
        ) : overview ? (
          <>
            <p className={`text-xs mb-5 flex items-center gap-1.5 ${muted}`}>
              <Clock className="w-3.5 h-3.5 shrink-0" />
              {formatChallengeDateTime(overview.startDate)} — {formatChallengeDateTime(overview.endDate)}
            </p>

            {/* Tiles */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <StatTile
                icon={Users}
                label="Total Accounts"
                value={overview.totalAccounts.toLocaleString('en-US')}
                tone="#2563eb"
                card={card}
                muted={muted}
                text={text}
              />
              <StatTile
                icon={BarChart3}
                label="Total Lots"
                value={formatLots(overview.totalLots)}
                tone={THEME}
                card={card}
                muted={muted}
                text={text}
              />
              <StatTile
                icon={DollarSign}
                label="Total Earnings"
                value={formatMoney(overview.totalEarnings)}
                tone="#16a34a"
                card={card}
                muted={muted}
                text={text}
              />
            </div>
          </>
        ) : null}

        {/* Filters */}
        <div className={`rounded-2xl border p-3 sm:p-4 shadow-sm mb-5 ${card}`}>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 ${muted}`} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                placeholder="Search by user code or account number…"
                className={`${inputCls} !rounded-full pl-11 focus:border-red-800 focus:ring-red-800/15`}
              />
            </div>

            <div className="flex gap-2 shrink-0">
              <button
                type="button"
                onClick={handleApplyFilters}
                className="px-5 py-2.5 rounded-full text-sm font-semibold text-white transition"
                style={{ backgroundColor: THEME }}
              >
                Apply
              </button>
              <button
                type="button"
                onClick={handleResetFilters}
                disabled={!hasActiveFilters && !searchTerm}
                className={`px-4 py-2.5 rounded-full text-sm font-medium border inline-flex items-center gap-1.5 disabled:opacity-40 ${
                  isDarkMode ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Overall progress toward the challenge's 50-lot / $ target (all accounts combined) */}
        {overview ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            <TintedProgressCard
              icon={Target}
              tone="#f97316"
              label={`Remaining Lots (Min ${formatLots(overview.minimumLots)})`}
              value={formatLots(overallRemainingLots)}
              barPct={overallProgressPct}
              barLabel={`${overallProgressPct}%`}
              showCheck={overallProgressPct >= 100}
            />
            <TintedProgressCard
              icon={Trophy}
              tone="#16a34a"
              label={`Progress toward ${formatMoney(overview.minimumLots * overview.commissionPerLot)}`}
              value={`${overallProgressPct}%`}
              barPct={overallProgressPct}
              barLabel={`${overallProgressPct}%`}
              showCheck={overallProgressPct >= 100}
            />
          </div>
        ) : null}

        {/* Participants table */}
        <div className={`rounded-2xl border shadow-sm overflow-hidden ${card}`}>
          <div className={`px-5 py-4 border-b flex items-center gap-2 ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
            <Trophy className="w-4 h-4" style={{ color: THEME }} />
            <h2 className={`text-sm font-semibold ${text}`}>Progress</h2>
            <span className={`text-xs ml-auto ${muted}`}>{totalRecords.toLocaleString('en-US')} accounts</span>
          </div>

          {rowsError ? (
            <div className="text-center py-16 px-4 text-sm text-amber-700">{rowsError}</div>
          ) : loadingRows ? (
            <div className="flex justify-center py-16">
              <NewtonsCradleLoader />
            </div>
          ) : rows.length === 0 ? (
            <div className={`text-center py-16 px-4 ${muted}`}>
              <p className="text-sm">
                {hasActiveFilters ? 'No accounts match your filters.' : 'No referred accounts in this challenge window yet.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px]">
                <thead>
                  <tr
                    className={`text-left text-[11px] font-semibold uppercase tracking-wide border-b ${
                      isDarkMode ? 'bg-gray-800/60 border-gray-800 text-gray-400' : 'bg-gray-50 border-gray-100 text-gray-500'
                    }`}
                  >
                    <th className="px-4 py-3">Created DateTime</th>
                    <th className="px-3 py-3">User Code</th>
                    <th className="px-3 py-3">Account No</th>
                    <th className="px-3 py-3">Is Deposit</th>
                    <th className="px-3 py-3">Lots Trade</th>
                    <th className="px-3 py-3">Earnings</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDarkMode ? 'divide-gray-800' : 'divide-gray-50'}`}>
                  {rows.map((row, idx) => (
                    <tr
                      key={row.id}
                      className={
                        idx % 2 === 1
                          ? isDarkMode ? 'bg-gray-800/20' : 'bg-gray-50/60'
                          : ''
                      }
                    >
                      <td className={`px-4 py-3.5 text-sm whitespace-nowrap ${muted}`}>{formatChallengeDateTime(row.createdDateTime)}</td>
                      <td className={`px-3 py-3.5 text-sm font-semibold ${text}`}>{row.userCode}</td>
                      <td className={`px-3 py-3.5 text-sm font-mono ${text}`}>{row.accountNo}</td>
                      <td className="px-3 py-3.5">
                        <DepositBadge isDeposit={row.isDeposit} />
                      </td>
                      <td className={`px-3 py-3.5 text-sm font-semibold ${text}`}>{formatLots(row.lotsTrade)}</td>
                      <td className="px-3 py-3.5 text-sm font-bold" style={{ color: THEME }}>{formatMoney(row.earnings)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loadingRows && totalRecords > 0 && (
            <div className={`px-5 py-4 border-t flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${muted}`}>Show</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(e.target.value);
                      setCurrentPage(1);
                    }}
                    className={`rounded-lg border px-2 py-1.5 text-xs outline-none min-w-[3.5rem] ${
                      isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
                    }`}
                  >
                    {PAGE_SIZE_OPTIONS.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                  <span className={`text-xs ${muted}`}>entries</span>
                </div>
                <p className={`text-xs ${muted}`}>
                  Showing {showingFrom} to {showingTo} of {totalRecords} entries
                </p>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed ${
                      isDarkMode ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Prev
                  </button>
                  <span className={`px-2 text-xs ${muted}`}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    type="button"
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage((p) => p + 1)}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed ${
                      isDarkMode ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChallengesList;
